import { tracker } from '@lessonup/analytics';
import { useLocale } from '@lessonup/client-integration';
import {
  Button,
  Checkbox,
  ErrorBanner,
  SelectField,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
  TextField,
  useForm,
  Validation,
} from '@lessonup/ui-components';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiToolboxGenerateAssignmentInput } from '../../../types/graphql';
import { getLanguageValue } from '../../../utils/language/languageOptions';
import { AssignmentGenerator } from '../AiToolbox.types';
import { i18nextNamespaceAiTools } from '../AiToolbox.utils';
import { useLessonUrlFromContext } from '../context/AiToolboxContext';
import { ComprehensionLevelSelector } from '../helpers/ComprehensionLevelSelector';
import { DefaultToolWrapper } from '../helpers/DefaultToolWrapper';
import { LanguageSelector, transformLanguageFormData } from '../helpers/LanguageSelector';
import { LessonupUrlOrSubject } from '../helpers/LessonupUrlOrSubject';
import { TipTapEditorSectionAiTools } from '../helpers/TipTapEditorSectionAiTools';
import { AiToolboxFormWrapper } from '../helpers/ToolForm';
import { useAiToolboxSubscription } from '../hooks/useAiToolboxSubscription';

export interface AiToolboxAssignmentProps {
  generator: AssignmentGenerator;
  country?: string;
}

export function AiToolboxAssignment({ generator }: AiToolboxAssignmentProps) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const lessonupUrl = useLessonUrlFromContext();
  const currentLanguage = useLocale().language;

  const methods = useForm<AiToolboxGenerateAssignmentInput>({
    defaultValues: {
      lessonupUrl,
      resourcesAllowed: [],
      assignmentFormat: [],
      language: getLanguageValue(currentLanguage),
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;
  const { subscribe, html, isSubscribed, isCompleted } = useAiToolboxSubscription(generator);

  const onSubmit = (data: AiToolboxGenerateAssignmentInput) => {
    const durationMinutes = parseInt(data.durationMinutes as unknown as string, 10);
    const correctedFormData = transformLanguageFormData(data);
    const formData = {
      ...correctedFormData,
      durationMinutes,
    };

    tracker.events.aiToolsTaskCreate({
      educationalLevel: data.comprehensionLevel ?? '',
      taskType: data.assignmentType,
      taskDuration: durationMinutes,
      allowedResources: (data.resourcesAllowed.filter(Boolean) ?? []) as string[],
      assignmentFormat: (data.assignmentFormat.filter(Boolean) ?? []) as string[],
      lessonAsInput: !!data.lessonupUrl,
      promptLength: data.subject?.length ?? 0,
      languageOutput: data.language ?? '',
    });

    subscribe(formData);
  };

  return (
    <DefaultToolWrapper>
      <AiToolboxFormWrapper generator={generator}>
        <FormProvider {...methods}>
          <SpaceBetween direction="y" spacing={spacing.size32}>
            <LessonupUrlOrSubject lessonUrlName="lessonupUrl" subjectName="subject" />

            <ComprehensionLevelSelector<AiToolboxGenerateAssignmentInput> register={register} required />

            <SelectField
              size="medium"
              label={t('assignmentType')}
              options={[
                { value: 'individual', label: t('assignmentTypeIndividual') },
                { value: 'group', label: t('assignmentTypeGroup') },
                { value: 'homework', label: t('assignmentTypeHomework') },
              ]}
              {...register('assignmentType', { required: true })}
            />

            <SelectField
              size="medium"
              label={t('assignmentDuration')}
              options={[
                { value: '10', label: `10 ${t('assignmentDurationMinutes')}` },
                { value: '30', label: `30 ${t('assignmentDurationMinutes')}` },
                { value: '60', label: `60 ${t('assignmentDurationMinutes')}` },
                { value: '90', label: `90 ${t('assignmentDurationMinutes')}` },
              ]}
              {...register('durationMinutes', { required: true })}
            />

            <StyledFieldset>
              <TextElement textStyle="label" weight="bold" as="legend" size="medium">
                {t('assignmentAllowedResources')}
              </TextElement>

              {[
                { id: 'internet', label: t('assignmentResourceInternet'), value: 'internet' },
                { id: 'books', label: t('assignmentResourceBooks'), value: 'books' },
                { id: 'calculators', label: t('assignmentResourceCalculators'), value: 'calculators' },
              ].map(({ id, label, value }) => (
                <Checkbox
                  key={id}
                  id={id}
                  label={label}
                  value={value}
                  {...register('resourcesAllowed', {
                    validate: (value) => value?.length > 0 || t('errorMessageMinLength1'),
                  })}
                />
              ))}

              {errors.resourcesAllowed && errors.resourcesAllowed.message && (
                <StyledValidationMessageWrapper>
                  <Validation id="resourcesAllowed" type="negative" message={errors.resourcesAllowed.message} />
                </StyledValidationMessageWrapper>
              )}
            </StyledFieldset>

            <StyledFieldset>
              <TextElement textStyle="label" weight="bold" as="legend" size="medium">
                {t('assignmentFormat')}
              </TextElement>

              {[
                { id: 'essay', label: t('assignmentFormatEssay'), value: 'essay' },
                { id: 'presentation', label: t('assignmentFormatPresentation'), value: 'presentation' },
                { id: 'moodboard', label: t('assignmentFormatMoodboard'), value: 'moodboard' },
                { id: 'practical', label: t('assignmentFormatPractical'), value: 'practical' },
                { id: 'multiple-choice', label: t('assignmentFormatMultipleChoice'), value: 'multiple-choice' },
                { id: 'open', label: t('assignmentFormatOpen'), value: 'open' },
                { id: 'short-answer', label: t('assignmentFormatShortAnswer'), value: 'short-answer' },
              ].map(({ id, label, value }) => (
                <Checkbox
                  key={id}
                  id={id}
                  label={label}
                  value={value}
                  {...register('assignmentFormat', {
                    validate: (value) => value?.length > 0 || t('errorMessageMinLength1'),
                  })}
                />
              ))}

              {errors.assignmentFormat && errors.assignmentFormat.message && (
                <StyledValidationMessageWrapper>
                  <Validation id="assignmentFormat" type="negative" message={errors.assignmentFormat.message} />
                </StyledValidationMessageWrapper>
              )}
            </StyledFieldset>

            <TextField
              size="medium"
              type="text"
              label={t('assignmentLearningObjective')}
              placeholder={t('assignmentLearningObjectivePlaceholder')}
              maxLength={1000}
              {...register('learningObjective', { required: true })}
            />

            <LanguageSelector<AiToolboxGenerateAssignmentInput> />

            <ErrorBanner />

            <Button loading={isSubscribed} onClick={handleSubmit(onSubmit)}>
              {t('assignmentCta')}
            </Button>
          </SpaceBetween>
        </FormProvider>
      </AiToolboxFormWrapper>
      {html && <TipTapEditorSectionAiTools html={html} generator={generator} generatorDone={isCompleted} />}
    </DefaultToolWrapper>
  );
}

const StyledFieldset = styled.fieldset`
  all: unset;
`;

const StyledValidationMessageWrapper = styled.div`
  margin-top: ${spacing.size8};
`;
